import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  FiCalendar,
  FiCopy,
  FiDownload,
  FiDollarSign,
  FiMail,
  FiMessageCircle,
  FiPrinter,
  FiShield,
} from 'react-icons/fi';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';
import {
  LEAVE_BEHIND_CLERK_BLURB,
  LEAVE_BEHIND_EN,
  LEAVE_BEHIND_PDF,
  LEAVE_BEHIND_PDF_DOWNLOAD,
} from '@/lib/leave-behind';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

const CLERK_EMAIL = 'contact@harborcoparent.com';
const CLERK_MAILTO = [
  `mailto:${CLERK_EMAIL}`,
  '?subject=',
  encodeURIComponent('Harbor leave-behind for Family Court Services'),
].join('');

const TOOLS = [
  { title: 'Timestamped messages', Icon: FiMessageCircle },
  { title: 'Shared custody calendar', Icon: FiCalendar },
  { title: 'Expense splits and receipts', Icon: FiDollarSign },
  { title: 'Records you can export', Icon: FiDownload },
] as const;

function handleDownload(language: 'en' | 'es') {
  track('Courts Leave Behind Downloaded', { language });
}

function handleClerkEmailClick() {
  track('Click Courts Contact Email');
}

function CourtsPage() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    track('Courts Screen');
  }, []);

  const handleCopyBlurb = () => {
    track('Courts Blurb Copied');
    navigator.clipboard.writeText(LEAVE_BEHIND_CLERK_BLURB).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      (err) => {
        console.error('Failed to copy clerk blurb:', err);
      },
    );
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Harbor for Family Court Services and Self-Help'
        description='Stock Harbor as a parent option without endorsing it. Download English and Spanish one-pagers, copy a short resource-list blurb, and see fee-waiver and named-app notes.'
        path='/courts'
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='max-w-3xl mx-auto px-6'>
          <p
            className='text-primary font-semibold text-sm uppercase tracking-wide mb-3'
            style={{ ...nunito, fontWeight: 600 }}
          >
            For Family Court Services and Self-Help
          </p>
          <h1
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Harbor is a parent option you can stock without endorsing it.
          </h1>
          <p
            className='text-xl text-gray-600 leading-relaxed'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Print the English and Spanish one-pagers for the waiting-room rack,
            or copy the short description below into an existing resource list.
          </p>
          <div className='mt-8 rounded-2xl border border-primary/20 bg-white px-5 py-4'>
            <p
              className='text-sm leading-relaxed text-gray-700'
              style={nunito}
            >
              {LEAVE_BEHIND_EN.disclaimer}
            </p>
          </div>
        </div>
      </section>

      <main className='max-w-3xl mx-auto px-6 py-14'>
        <section className='mb-14'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-3'
            style={{ ...nunito, fontWeight: 700 }}
          >
            What parents get
          </h2>
          <p className='text-gray-600 leading-relaxed mb-6' style={nunito}>
            Parents use Harbor for timestamped messages, a shared custody
            calendar, expense splits, and records they can export. $9.99/month
            or $49.99/year per person. One parent can start; the other does not
            have to join.
          </p>
          <ul className='grid gap-3 sm:grid-cols-2'>
            {TOOLS.map(({ title, Icon }) => (
              <li
                key={title}
                className='flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3'
              >
                <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                  <Icon className='h-[18px] w-[18px]' aria-hidden='true' />
                </span>
                <span
                  className='text-gray-900'
                  style={{ ...nunito, fontWeight: 600 }}
                >
                  {title}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className='mb-14'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-3'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Stock the rack
          </h2>
          <p className='text-gray-600 leading-relaxed mb-6' style={nunito}>
            US Letter, one side. Print as many as you need. Same files we can
            email as attachments.
          </p>
          <div className='flex flex-wrap gap-3'>
            <a
              href={LEAVE_BEHIND_PDF.en}
              download={LEAVE_BEHIND_PDF_DOWNLOAD.en}
              onClick={() => handleDownload('en')}
              className='inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-white hover:bg-primary-hover'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FiDownload className='h-4 w-4' />
              Download English PDF
            </a>
            <a
              href={LEAVE_BEHIND_PDF.es}
              download={LEAVE_BEHIND_PDF_DOWNLOAD.es}
              onClick={() => handleDownload('es')}
              className='inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-white hover:bg-primary-hover'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FiDownload className='h-4 w-4' />
              Download Spanish PDF
            </a>
          </div>
          <div className='mt-4 flex flex-wrap gap-4 text-sm'>
            <a
              href='/leave-behind?print=1'
              className='inline-flex items-center gap-2 font-semibold text-primary hover:underline'
              style={nunito}
            >
              <FiPrinter className='h-4 w-4' />
              Preview / print English
            </a>
            <a
              href='/leave-behind/es?print=1'
              className='inline-flex items-center gap-2 font-semibold text-primary hover:underline'
              style={nunito}
            >
              <FiPrinter className='h-4 w-4' />
              Preview / print Spanish
            </a>
          </div>
        </section>

        <section className='mb-14'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-3'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Paste into a resource list
          </h2>
          <p className='text-gray-600 leading-relaxed mb-4' style={nunito}>
            Three sentences you can drop into an existing parenting-app sheet.
            Not a recommendation.
          </p>
          <div className='rounded-2xl border border-gray-200 bg-gray-50 p-5'>
            <p className='text-gray-800 leading-relaxed' style={nunito}>
              {LEAVE_BEHIND_CLERK_BLURB}
            </p>
            <button
              type='button'
              onClick={handleCopyBlurb}
              className='mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50'
              style={{ ...nunito, fontWeight: 600 }}
            >
              <FiCopy className='h-4 w-4' />
              {copied ? 'Copied' : 'Copy blurb'}
            </button>
          </div>
        </section>

        <section className='mb-14 grid gap-4 sm:grid-cols-2'>
          <div className='rounded-2xl border border-gray-200 p-6'>
            <h3
              className='font-semibold text-gray-900 mb-2'
              style={{ ...nunito, fontWeight: 600 }}
            >
              Need help paying?
            </h3>
            <p className='text-gray-600 leading-relaxed mb-4' style={nunito}>
              Parents who cannot afford the subscription can request a Harbor
              fee waiver with a court fee waiver or benefits letter. That is
              not a court program.
            </p>
            <Link
              to='/fee-waivers'
              className='font-semibold text-primary hover:underline'
              style={nunito}
            >
              Fee waivers
            </Link>
          </div>
          <div className='rounded-2xl border border-gray-200 p-6'>
            <FiShield className='w-6 h-6 text-primary mb-3' />
            <h3
              className='font-semibold text-gray-900 mb-2'
              style={{ ...nunito, fontWeight: 600 }}
            >
              If an order names another app
            </h3>
            <p className='text-gray-600 leading-relaxed' style={nunito}>
              If a court order already names another app, the parent should use
              that app. Harbor is an option, not a replacement for a named
              platform.
            </p>
          </div>
        </section>

        <section className='mb-14 text-sm text-gray-500' style={nunito}>
          Counsel looking for reports or sample order language can use{' '}
          <Link to='/learn/lawyers' className='text-primary hover:underline'>
            Harbor for counsel
          </Link>{' '}
          and{' '}
          <Link
            to='/court-order-language'
            className='text-primary hover:underline'
          >
            court-order language
          </Link>
          .
        </section>

        <section className='rounded-2xl bg-primary px-6 py-10 text-center'>
          <h2
            className='text-2xl font-bold text-white mb-3'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Need more copies or a drop-off?
          </h2>
          <p className='text-white/85 mb-6' style={nunito}>
            Email {CLERK_EMAIL}. Fee-waiver requests go through the parent
            page, not this inbox.
          </p>
          <a
            href={CLERK_MAILTO}
            onClick={handleClerkEmailClick}
            className='inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-primary font-semibold px-8 py-4 rounded-lg transition-all text-lg'
            style={{ ...nunito, fontWeight: 600 }}
          >
            <FiMail className='w-5 h-5' />
            Email {CLERK_EMAIL}
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/courts')({
  component: CourtsPage,
});
